import React, { useEffect, useState } from 'react';

import { T } from 'react-translator-component';
import styled from 'styled-components';

import { getIntegrationStationDetail } from '../../../hooks/interoperability';
import {
  IntegrationStation,
  StationConnector,
} from '../../../types/responses/Interoperability';
import StatusBadge from '../../atoms/StatusBadge';
import { directionHint, directionLabel } from '../../organisms/Interoperability/contractRules';
import {
  connectorImage,
  connectorPower,
  connectorStatusLabel,
  connectorStatusStyle,
  connectorTitle,
  connectorTypeLabel,
} from '../../organisms/Interoperability/StationsTab/connectorLabels';
import {
  heartbeatLabel,
  issueConsequence,
  issueLabel,
  issueSeverity,
  sortedIssues,
  SEVERITY_STYLE,
  stationLocation,
  stationTitle,
} from '../../organisms/Interoperability/StationsTab/stationIssues';



const Card = styled.div`
  background: #fff;
  border: 1px solid #e9ebee;
  border-radius: 8px;
  padding: 14px 16px;
  margin-bottom: 10px;
`;

const CardTitle = styled.h4`
  margin: 0 0 6px;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #8a9099;
`;

const Row = styled.p`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  margin: 0;
  padding: 6px 0;
  border-bottom: 1px solid #f2f4f6;
  font-size: 12.5px;
  color: #6b7280;

  &:last-of-type {
    border-bottom: none;
  }
`;

const Value = styled.span`
  color: #2d3339;
  text-align: right;
`;

const Mono = styled.span`
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 12px;
`;

const Hint = styled.p`
  margin: 6px 0 0;
  font-size: 11.5px;
  color: #8a9099;
`;

const Issue = styled.div<{ bg: string; fg: string }>`
  background: ${({ bg }) => bg};
  border-radius: 6px;
  padding: 8px 10px;
  margin-top: 8px;

  strong {
    color: ${({ fg }) => fg};
    font-size: 12.5px;
  }

  p {
    margin: 2px 0 0;
    font-size: 11.5px;
    color: #6b7280;
  }
`;

const Ok = styled.p`
  margin: 0;
  font-size: 12.5px;
  color: #1e7a45;
`;

const Loading = styled.p`
  margin: 0;
  font-size: 12px;
  color: #8a9099;
`;

const Connector = styled.div`
  border-top: 1px solid #f2f4f6;
  padding: 8px 0 6px;

  &:first-of-type {
    border-top: none;
  }
`;

const ConnectorHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;

  strong {
    font-size: 12.5px;
    color: #2d3339;
  }
`;

/**
 * O tipo com a figura ao lado.
 *
 * Sem imagem quando o tipo e desconhecido ou vazio: <img> apontando para nada desenha o
 * icone de figura quebrada, que na tela parece defeito do sistema e nao dado faltando.
 */
const TypeFact = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
`;

const PlugImage = styled.img`
  width: 16px;
  height: 16px;
  object-fit: contain;
`;

const ConnectorFacts = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px 10px;
  margin-top: 3px;
  font-size: 11.5px;
  color: #6b7280;
`;

/** O texto de falha que o carregador manda: sai cru, porque e o que o log dele diz. */
const Unusual = styled.p`
  margin: 3px 0 0;
  font-size: 11.5px;
  color: #b02525;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
`;

export type StationDrawerDetailsProps = {
  tenantId: string;
  station: IntegrationStation;
};

/**
 * Detalhe de uma estação da integração (MV2-1706).
 *
 * A listagem cabe numa linha; o que a pessoa precisa para pedir a correção não —
 * o id da estação, a coordenada exata, e o que cada problema causa no app do
 * parceiro. É por isso que o detalhe existe, e não para repetir a linha maior.
 */
export const StationDrawerDetails: React.FC<StationDrawerDetailsProps> = ({
  tenantId,
  station,
}) => {
  const issues = sortedIssues(station.issues || []);

  const [connectors, setConnectors] = useState<StationConnector[] | null>(null);
  const [loadError, setLoadError] = useState(false);

  /**
   * Os conectores vem em chamada propria, na abertura do painel.
   *
   * A listagem traz so a contagem: carregar conector de 529 estacoes para abrir uma
   * seria pagar por todas as que ninguem vai abrir.
   */
  useEffect(() => {
    let current = true;

    setConnectors(null);
    setLoadError(false);

    getIntegrationStationDetail(tenantId, station.charge_box_id)
      .then((detail) => {
        if (current) {
          setConnectors(detail?.connectors || []);
        }
      })
      .catch(() => {
        if (current) {
          setLoadError(true);
        }
      });

    // Trocar de estacao antes da resposta chegar nao pode pintar o conector da
    // anterior no painel da nova.
    return () => {
      current = false;
    };
  }, [tenantId, station.charge_box_id]);

  return (
    <div>
      <Card>
        <CardTitle>{T('Station')}</CardTitle>

        <Row>
          {T('Name')}
          <Value>{stationTitle(station)}</Value>
        </Row>

        <Row>
          {T('Number')}
          <Value>{station.charge_box_number ?? '—'}</Value>
        </Row>

        <Row>
          {T('Identifier')}
          <Value>
            <Mono>{station.charge_box_id}</Mono>
          </Value>
        </Row>

        <Row>
          {T('Connectors')}
          <Value>{station.connector_count ?? 0}</Value>
        </Row>

        <Row>
          {T('Enabled')}
          <Value>{station.enabled ? T('Yes') : T('No')}</Value>
        </Row>
      </Card>

      <Card>
        <CardTitle>{T('Contract')}</CardTitle>

        <Row>
          {T('Type')}
          <Value>{directionLabel(station.direction)}</Value>
        </Row>

        <Row>
          {T('Partners')}
          <Value>
            {station.partners && station.partners.length > 0
              ? station.partners.map((partner) => partner.name).join(', ')
              : T('none')}
          </Value>
        </Row>

        <Row>
          {T('Owner account')}
          <Value>{station.owner_name || '—'}</Value>
        </Row>

        <Hint>{directionHint(station.direction)}</Hint>
      </Card>

      <Card>
        <CardTitle>{T('Registration data')}</CardTitle>

        <Row>
          {T('Location')}
          <Value>{stationLocation(station)}</Value>
        </Row>

        {/*
          Coordenada crua, e nao arredondada: quem vai corrigir precisa comparar com o
          que esta no cadastro da estacao, e um 0,0 tem de aparecer como 0,0.
        */}
        <Row>
          {T('Coordinates')}
          <Value>
            {station.latitude !== null && station.longitude !== null ? (
              <Mono>
                {station.latitude}, {station.longitude}
              </Mono>
            ) : (
              '—'
            )}
          </Value>
        </Row>

        <Row>
          {T('Last signal')}
          <Value>{heartbeatLabel(station)}</Value>
        </Row>

        <Row>
          {T('Status now')}
          <Value>{station.online ? T('Online') : T('Offline')}</Value>
        </Row>
      </Card>

      <Card>
        <CardTitle>
          {T('Connectors')} {connectors ? `(${connectors.length})` : ''}
        </CardTitle>

        {!connectors && !loadError && <Loading>{T('Loading...')}</Loading>}

        {loadError && <Loading>{T('Could not load the connectors.')}</Loading>}

        {connectors && connectors.length === 0 && (
          <Loading>{T('This station has no connector registered.')}</Loading>
        )}

        {connectors &&
          connectors.map((connector) => {
            const style = connectorStatusStyle(connector.status);

            return (
              <Connector key={connector.seq}>
                <ConnectorHead>
                  <strong>{connectorTitle(connector)}</strong>
                  <StatusBadge tone={{ bg: style.bg, fg: style.fg, dot: style.dot }}>
                    {connectorStatusLabel(connector.status)}
                  </StatusBadge>
                </ConnectorHead>

                {/*
                  Tipo e potencia lado a lado: e por eles que o motorista decide se a
                  estacao serve para o carro dele.
                */}
                <ConnectorFacts>
                  {/*
                    A figura do plugue antes do nome: e por ela que se reconhece o conector
                    de relance, e o nome sozinho ("CCS Type 2") exige leitura.
                  */}
                  <TypeFact>
                    {connectorImage(connector.connector_type) && (
                      <PlugImage
                        src={connectorImage(connector.connector_type) as string}
                        alt=""
                      />
                    )}
                    {connectorTypeLabel(connector)}
                  </TypeFact>
                  <span>{connectorPower(connector)}</span>
                  <span>{connector.has_tariff ? T('own tariff') : T('no own tariff')}</span>
                </ConnectorFacts>

                {connector.unusual_status && (
                  <Unusual>{connector.unusual_status}</Unusual>
                )}
              </Connector>
            );
          })}
      </Card>

      <Card>
        <CardTitle>{T('What is wrong')}</CardTitle>

        {issues.length === 0 && <Ok>{T('Nothing: this station is correctly registered.')}</Ok>}

        {issues.map((issue) => {
          const style = SEVERITY_STYLE[issueSeverity(issue)];

          return (
            <Issue key={issue} bg={style.bg} fg={style.fg}>
              <strong>{issueLabel(issue)}</strong>
              {/* A consequencia e o que faz a pessoa decidir se corrige hoje. */}
              <p>{issueConsequence(issue)}</p>
            </Issue>
          );
        })}
      </Card>
    </div>
  );
};
